/**
 * Mode Shell spine read contracts — RO-3-00c / #44470.
 *
 * The `_modes/` Mode Shell (`mode-page`, `domain-views`, `lane-page`, section
 * title toolbars, …) resolves tenant, opens the DB and calls `loadModeData` /
 * CRM list helpers / lane rows **inline in the render path**. A Vite client
 * cannot. These READs are the HTTP seam that rewrite needs: each response is
 * the same projection the Next server component assembled in-process.
 *
 * Source of truth (do not repoint live Next call sites — PRD D6 / ccb89192):
 *   excellent/app/_modes/mode-page.tsx
 *   excellent/app/_modes/domain-views.tsx
 *   excellent/app/_modes/lane-page.tsx
 *   excellent/app/_modes/*-title-toolbar.tsx
 *   excellent/src/modes/mode-data.ts
 *
 * All ops are pure GET / pure SELECT. `READ` is honest, not merely method-shaped.
 */

import { z } from "zod";
import type { Operation } from "../types";

// ── shared shapes ────────────────────────────────────────────────────────────

const modeSlug = z.string().min(1);
const modeObjectProp = z.object({
  uid: z.string(),
  kind: z.string(),
  title: z.string(),
  state: z.string(),
  ownerLabel: z.string().nullable(),
  targetOutcome: z.string().nullable(),
  updatedAt: z.string(),
});
export type ModeObjectProp = z.infer<typeof modeObjectProp>;

const modeData = z.object({
  rows: z.array(modeObjectProp),
  phaseCounts: z.record(z.string(), z.number()),
  total: z.number(),
});
export type ModeData = z.infer<typeof modeData>;

const customDomain = z.object({
  slug: z.string(),
  name: z.string(),
  icon: z.string().nullable().optional(),
  mode: z.string(),
  member_kinds: z.array(z.string()),
  home_view_uid: z.string().nullable().optional(),
  sort_order: z.number(),
  enabled: z.boolean(),
});
export type ModeCustomDomain = z.infer<typeof customDomain>;

const laneRow = z.object({
  uid: z.string(),
  slug: z.string(),
  label: z.string(),
  status: z.string(),
  description: z.string().nullable(),
  position: z.number().optional(),
  object_kinds: z.array(z.string()).optional(),
  underlying_domain: z.string().nullable().optional(),
});
export type ModeLaneRow = z.infer<typeof laneRow>;

// ── modes.data — commitment index (loadModeData) ─────────────────────────────

const dataQuery = z.object({
  mode: modeSlug,
  /** Optional kinds override (nested shell: Operations ▸ Finance). */
  kinds: z.string().optional(),
});
export type ModesDataQuery = z.infer<typeof dataQuery>;

const dataOp: Operation<void, ModesDataQuery, void, ModeData> = {
  method: "GET",
  path: () => "/api/modes/data",
  query: dataQuery,
  response: modeData,
};

// ── modes.shell — mode-page bootstrap (tenant + custom domains + lane + flags)

const shellQuery = z.object({
  mode: modeSlug,
  area: z.string().optional(),
  projectSlug: z.string().optional(),
});
export type ModesShellQuery = z.infer<typeof shellQuery>;

const shellResponse = z.object({
  workspaceId: z.number(),
  customDomains: z.array(customDomain),
  /** Resolved data-defined Lane for `area`, or null. */
  lane: laneRow.nullable(),
  /** Project exists under this workspace for `projectSlug`, or null. */
  project: z
    .object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
    })
    .nullable(),
  pulse: z.object({
    dashboard: z.boolean(),
    selfNav: z.boolean(),
    dynamic: z.boolean(),
  }),
});
export type ModesShell = z.infer<typeof shellResponse>;

const shellOp: Operation<void, ModesShellQuery, void, ModesShell> = {
  method: "GET",
  path: () => "/api/modes/shell",
  query: shellQuery,
  response: shellResponse,
};

// ── modes.resolveSection — mode-section-route lane/domain existence ──────────

const resolveSectionQuery = z.object({
  mode: modeSlug,
  section: z.string().min(1),
});
export type ModesResolveSectionQuery = z.infer<typeof resolveSectionQuery>;

const resolveSectionResponse = z.object({
  /** False when neither a domain alias nor a lane matches → client 404. */
  found: z.boolean(),
  area: z.string(),
  hiddenAlias: z.boolean(),
  isLane: z.boolean(),
  lane: laneRow.nullable(),
});
export type ModesResolveSection = z.infer<typeof resolveSectionResponse>;

const resolveSectionOp: Operation<void, ModesResolveSectionQuery, void, ModesResolveSection> = {
  method: "GET",
  path: () => "/api/modes/resolve-section",
  query: resolveSectionQuery,
  response: resolveSectionResponse,
};

// ── modes.lane — LanePage cockpit data ───────────────────────────────────────

const laneQuery = z.object({
  mode: modeSlug,
  slug: z.string().min(1),
});
export type ModesLaneQuery = z.infer<typeof laneQuery>;

const laneLoop = z.object({
  id: z.number(),
  uid: z.string().nullable(),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  domain: z.string().nullable(),
  lane_uid: z.string().nullable(),
});
export type ModesLaneLoop = z.infer<typeof laneLoop>;

const laneGoal = z.object({
  id: z.number(),
  uid: z.string().nullable(),
  kind: z.enum(["agentic", "strategic"]),
  slug: z.string(),
  title: z.string(),
  outcome: z.string().nullable(),
  status: z.string(),
  role: z.string(),
  task_count: z.number(),
  done_task_count: z.number(),
  cancelled_task_count: z.number(),
  // Pass-through definition fields so the client can buildFinishLine/parseMilestones.
  metric_label: z.string().nullable().optional(),
  metric_baseline: z.string().nullable().optional(),
  metric_current: z.string().nullable().optional(),
  metric_target: z.string().nullable().optional(),
  metric_pct: z.number().nullable().optional(),
  criteria_json: z.string().nullable().optional(),
  milestones: z.string().nullable().optional(),
});
export type ModesLaneGoal = z.infer<typeof laneGoal>;

const laneResponse = z.object({
  lane: laneRow.nullable(),
  goals: z.array(laneGoal),
  bound: z.array(laneLoop),
  attachable: z.array(laneLoop),
  /** Commitment rows for the lane's kinds (Objects region). */
  objects: z.array(modeObjectProp),
});
export type ModesLane = z.infer<typeof laneResponse>;

const laneOp: Operation<void, ModesLaneQuery, void, ModesLane> = {
  method: "GET",
  path: () => "/api/modes/lane",
  query: laneQuery,
  response: laneResponse,
};

// ── modes.issuesChrome — issues title toolbar + detail header facts ──────────

const issuesChromeQuery = z.object({
  projectSlug: z.string().optional(),
  /** Comma statuses when the page already resolved them (optional). */
  status: z.string().optional(),
  range: z.string().optional(),
});
export type ModesIssuesChromeQuery = z.infer<typeof issuesChromeQuery>;

const issuesChromeResponse = z.object({
  scope: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("workspace") }),
    z.object({ kind: z.literal("project"), projectSlug: z.string(), projectName: z.string() }),
  ]),
  assignees: z.array(z.string()),
  projects: z.array(z.object({ slug: z.string(), name: z.string() })),
  counts: z.record(z.string(), z.number()),
  total: z.number(),
  done: z.number(),
  assigneeCount: z.number(),
  scopeLabel: z.string(),
});
export type ModesIssuesChrome = z.infer<typeof issuesChromeResponse>;

const issuesChromeOp: Operation<void, ModesIssuesChromeQuery, void, ModesIssuesChrome> = {
  method: "GET",
  path: () => "/api/modes/issues-chrome",
  query: issuesChromeQuery,
  response: issuesChromeResponse,
};

// ── modes.salesChrome — sales title toolbar reference lists ──────────────────

const salesChromeResponse = z.object({
  pipelines: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
      is_default: z.union([z.boolean(), z.number()]).nullable().optional(),
      stages: z.array(z.object({ id: z.number(), name: z.string() })),
    }),
  ),
  leadStatuses: z.array(z.object({ slug: z.string(), name: z.string() })),
  leadSources: z.array(z.object({ slug: z.string(), name: z.string() })),
  leads: z.array(z.object({ id: z.number(), display_name: z.string().nullable() })),
  owners: z.array(z.object({ id: z.number(), label: z.string() })),
  rawRows: z.array(
    z.object({
      kind: z.string(),
      label: z.string(),
      storage: z.string().nullable().optional(),
      records: z.number().nullable().optional(),
      fields: z.number().nullable().optional(),
      origin: z.string().nullable().optional(),
      created: z.string().nullable().optional(),
      href: z.string().nullable().optional(),
    }),
  ),
});
export type ModesSalesChrome = z.infer<typeof salesChromeResponse>;

const salesChromeOp: Operation<void, void, void, ModesSalesChrome> = {
  method: "GET",
  path: () => "/api/modes/sales-chrome",
  response: salesChromeResponse,
};

// ── modes.marketChrome — market title toolbar ────────────────────────────────

const marketChromeResponse = z.object({
  running: z.boolean(),
});
export type ModesMarketChrome = z.infer<typeof marketChromeResponse>;

const marketChromeOp: Operation<void, void, void, ModesMarketChrome> = {
  method: "GET",
  path: () => "/api/modes/market-chrome",
  response: marketChromeResponse,
};

// ── modes.workProjects — Work ▸ Projects board groups ────────────────────────

const workProjectsResponse = z.object({
  groups: z.array(z.unknown()),
});
export type ModesWorkProjects = z.infer<typeof workProjectsResponse>;

const workProjectsOp: Operation<void, void, void, ModesWorkProjects> = {
  method: "GET",
  path: () => "/api/modes/work-projects",
  response: workProjectsResponse,
};

// ── modes.workInbox — Work ▸ Inbox items ─────────────────────────────────────

const workInboxResponse = z.object({
  items: z.array(z.unknown()),
  actor: z.object({
    external_id: z.string(),
    display_name: z.string().nullable(),
  }),
});
export type ModesWorkInbox = z.infer<typeof workInboxResponse>;

const workInboxOp: Operation<void, void, void, ModesWorkInbox> = {
  method: "GET",
  path: () => "/api/modes/work-inbox",
  response: workInboxResponse,
};

// ── modes.financeLedger — Operations ▸ Finance ───────────────────────────────

const financeLedgerResponse = z.object({
  transactions: z.array(z.unknown()),
  capped: z.boolean(),
  stats: z.object({
    accounts: z.number(),
    counterparties: z.number(),
    volume: z.number().optional(),
  }).passthrough(),
  objectRows: z.array(z.unknown()),
});
export type ModesFinanceLedger = z.infer<typeof financeLedgerResponse>;

const financeLedgerOp: Operation<void, void, void, ModesFinanceLedger> = {
  method: "GET",
  path: () => "/api/modes/finance-ledger",
  response: financeLedgerResponse,
};

// ── modes.objectKinds — buildObjectRowsForKinds ──────────────────────────────

const objectKindsQuery = z.object({
  /** Comma-separated Golden-Schema kinds. */
  kinds: z.string().min(1),
});
export type ModesObjectKindsQuery = z.infer<typeof objectKindsQuery>;

const objectKindsResponse = z.object({
  rows: z.array(
    z.object({
      kind: z.string(),
      label: z.string(),
      storage: z.string().nullable().optional(),
      records: z.number().nullable().optional(),
      fields: z.number().nullable().optional(),
      origin: z.string().nullable().optional(),
      created: z.string().nullable().optional(),
      href: z.string().nullable().optional(),
    }),
  ),
});
export type ModesObjectKinds = z.infer<typeof objectKindsResponse>;

const objectKindsOp: Operation<void, ModesObjectKindsQuery, void, ModesObjectKinds> = {
  method: "GET",
  path: () => "/api/modes/object-kinds",
  query: objectKindsQuery,
  response: objectKindsResponse,
};

// ── modes.sectionExists — hasProjectSection for scoped mounts ────────────────

const sectionExistsQuery = z.object({
  projectSlug: z.string().min(1),
  sectionSlug: z.string().min(1),
});
export type ModesSectionExistsQuery = z.infer<typeof sectionExistsQuery>;

const sectionExistsResponse = z.object({
  exists: z.boolean(),
  projectId: z.number().nullable(),
});
export type ModesSectionExists = z.infer<typeof sectionExistsResponse>;

const sectionExistsOp: Operation<void, ModesSectionExistsQuery, void, ModesSectionExists> = {
  method: "GET",
  path: () => "/api/modes/section-exists",
  query: sectionExistsQuery,
  response: sectionExistsResponse,
};

// ── modes.issuesBoard — Issues page / Work ▸ Issues body (RO-3-03 / #44296) ───
//
// The Next page assembled scope + statusCounts + assignees + projects + the
// status-grouped task ledger + non-task extras + optimizer verdicts in-process.
// A client route cannot; this READ returns the same projection.

const issuesBoardQuery = z.object({
  project: z.string().optional(),
  /** Comma-separated task statuses (mirrors resolveTasksState). */
  status: z.string().optional(),
  assignee: z.string().optional(),
  range: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  view: z.string().optional(),
  section: z.string().optional(),
  empty: z.string().optional(),
  q: z.string().optional(),
  dept: z.string().optional(),
  mine: z.string().optional(),
});
export type ModesIssuesBoardQuery = z.infer<typeof issuesBoardQuery>;

const issuesBoardResponse = z.object({
  scope: z.union([
    z.object({ kind: z.literal("workspace") }),
    z.object({ kind: z.literal("project"), projectSlug: z.string() }),
  ]),
  assignees: z.array(z.unknown()),
  projects: z.array(z.object({ slug: z.string(), name: z.string() })),
  statusCounts: z.record(z.string(), z.number()),
  /** Status → ScopedTask[] (the board / grouped list source). */
  byStatus: z.record(z.string(), z.array(z.unknown())),
  /** Non-task WorkItem extras (workspace scope only). */
  extras: z.array(z.unknown()),
  /** Optimizer verdicts (runs + promotions + actions; workspace scope only). */
  verdicts: z.array(z.unknown()),
  /** Echo of the effective status filter the handler applied. */
  effectiveStatuses: z.array(z.string()),
});
export type ModesIssuesBoard = z.infer<typeof issuesBoardResponse>;

const issuesBoardOp: Operation<void, ModesIssuesBoardQuery, void, ModesIssuesBoard> = {
  method: "GET",
  path: () => "/api/modes/issues-board",
  query: issuesBoardQuery,
  response: issuesBoardResponse,
};

// ── modes.planner — Issues ▸ Calendar / /planner (RO-3-03 / #44296) ──────────

const plannerQuery = z.object({
  month: z.string().optional(),
});
export type ModesPlannerQuery = z.infer<typeof plannerQuery>;

const plannerResponse = z.object({
  monthIso: z.string(),
  scheduled: z.array(z.unknown()),
  unscheduled: z.array(z.unknown()),
  overdue: z.number(),
});
export type ModesPlanner = z.infer<typeof plannerResponse>;

const plannerOp: Operation<void, ModesPlannerQuery, void, ModesPlanner> = {
  method: "GET",
  path: () => "/api/modes/planner",
  query: plannerQuery,
  response: plannerResponse,
};

// ── modes.workDashboard — Work mode root dashboard (RO-3-03 / #44296) ────────

const workDashboardQuery = z.object({
  period: z.string().optional(),
  projectSlug: z.string().optional(),
});
export type ModesWorkDashboardQuery = z.infer<typeof workDashboardQuery>;

const workDashboardResponse = z.object({
  period: z.string(),
  goals: z.array(z.unknown()),
  dueRows: z.array(z.unknown()),
  projects: z.array(z.unknown()),
  roadmap: z.array(
    z.object({
      uid: z.string(),
      title: z.string(),
      task_count: z.number(),
      done_count: z.number(),
    }),
  ),
  base: z.string(),
});
export type ModesWorkDashboard = z.infer<typeof workDashboardResponse>;

const workDashboardOp: Operation<
  void,
  ModesWorkDashboardQuery,
  void,
  ModesWorkDashboard
> = {
  method: "GET",
  path: () => "/api/modes/work-dashboard",
  query: workDashboardQuery,
  response: workDashboardResponse,
};

// ── public barrel ────────────────────────────────────────────────────────────

export const modes = {
  data: dataOp,
  shell: shellOp,
  resolveSection: resolveSectionOp,
  lane: laneOp,
  issuesChrome: issuesChromeOp,
  salesChrome: salesChromeOp,
  marketChrome: marketChromeOp,
  workProjects: workProjectsOp,
  workInbox: workInboxOp,
  financeLedger: financeLedgerOp,
  objectKinds: objectKindsOp,
  sectionExists: sectionExistsOp,
  issuesBoard: issuesBoardOp,
  planner: plannerOp,
  workDashboard: workDashboardOp,
};

/** Flat list for bijection / registration assertions. */
export const MODES_READ_OPS = [
  modes.data,
  modes.shell,
  modes.resolveSection,
  modes.lane,
  modes.issuesChrome,
  modes.salesChrome,
  modes.marketChrome,
  modes.workProjects,
  modes.workInbox,
  modes.financeLedger,
  modes.objectKinds,
  modes.sectionExists,
  modes.issuesBoard,
  modes.planner,
  modes.workDashboard,
] as const;
